<template>
  <v-card>
    <v-toolbar v-if="!hideHeader" :dark="$vuetify.theme.dark" color="primary">
      <v-row align="center">
        <v-col cols="10" class="text-left">
          <v-toolbar-title class="textwithbackground--text">
            {{ $t('Состав торговой процедуры') }}
          </v-toolbar-title>
        </v-col>
        <v-col cols="2" class="text-right">
          <btn-icon-tip
              icon="add"
              icon-color="secondaryiconbtn"
              :tip="$t('Добавить позицию')"
              @click="openAddPositionDialog" />
        </v-col>
      </v-row>
    </v-toolbar>
    <v-row>
      <v-col>
        <v-data-table
            :headers="headers"
            :items="positions"
            :items-per-page="itemsPerPage"
            item-key="positionTemplateId"
            disable-sort-by
            :dense="dense"
            :loading="isLoading"
            :locale="systemLocale">
          <template v-slot:progress>
            <v-progress-linear :active="isLoading" color="loader" indeterminate />
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template v-slot:item.positionNumber="{ item }">
            {{ item.positionNumber }}
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template v-slot:item.positionDescription="{ item }">
            {{ item.positionDescription }}
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template v-slot:item.amount="{ item }">
            {{ item.amount }}
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template v-slot:item.startPremium="{ item }">
            {{ item.startPremium }}
          </template>
          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template v-slot:item.positionTemplateId="{ item }">
            <btn-icon-tip
                :id="`viewBtn-${item.positionTemplateId}`"
                :tip="$t('Просмотреть позицию')"
                icon="visibility"
                @click="openEditPositionDialog(item.positionTemplateId, true)" />
            <btn-icon-tip
                :id="`editBtn-${item.positionTemplateId}`"
                :tip="$t('Редактировать позицию')"
                icon="edit"
                @click="openEditPositionDialog(item.positionTemplateId, false )" />
            <btn-icon-tip
                :id="`deleteBtn-${item.positionTemplateId}`"
                :tip="$t('Удалить позицию')"
                icon="delete_forever"
                @click="openConfirmationBeforDelete(item.positionTemplateId)" />
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <confirm-dialog
        v-if="confirmDialog.show"
        :dialog-prop="confirmDialog"
        @close="closeDialog('confirmDialog')" />
    <add-position-template-dialog
        v-if="addPositionTemplateDialog.show"
        :dialog-prop="addPositionTemplateDialog"
        @close="closeDialog('addPositionTemplateDialog')" />
    <edit-position-template-dialog
        v-if="editPositionTemplateDialog.show"
        :dialog-prop="editPositionTemplateDialog"
        @close="closeDialog('editPositionTemplateDialog')" />
  </v-card>
</template>

<script>

  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import AddPositionTemplateDialog from '@/components/pages/shared/PositionTemplate/AddPositionTemplateDialog.vue'
  import EditPositionTemplateDialog from '@/components/pages/shared/PositionTemplate/EditPositionTemplateDialog.vue'
  import ConfirmDialog from '@/components/reused/common/ConfirmDialog.vue'
  import { mapActions } from 'vuex'
  import { POSITIONS_TEMPLATE_MANAGEMENT } from '@/constants/store.js'
  import { PREMIUM_TYPES } from '@/constants/business.js'

  export default {
    components: {
      BtnIconTip,
      AddPositionTemplateDialog,
      EditPositionTemplateDialog,
      ConfirmDialog
    },
    props: {
      dense: {
        type: Boolean,
        default: false
      },
      hideHeader: {
        type: Boolean,
        default: false
      },
      positions: {
        type: Array,
        required: true
      },
      lotTemplate: {
        type: Object,
        required: true,
        default: () => ({
          lotType: null,
          lotTemplateId: null,
          premiumType: null
        })
      },
      loading: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        isLoading: false,
        itemsPerPage: 5,
        addPositionTemplateDialog: {
          lotTemplate: null,
          show: false,
          callback: null
        },
        confirmDialog: {
          show: false,
          text: null,
          additional: null,
          callback: null
        },
        editPositionTemplateDialog: {
          positionTemplateId: null,
          lotTemplate: null,
          callback: null,
          show: false,
          readonly: false
        }
      }
    },
    computed: {
      headers () {
        let headers = [
          { text: this.$t('№ позиции'), align: 'start', sortable: false, value: 'positionNumber' },
          { text: this.$t('Наименование позиции'), align: 'start', sortable: false, value: 'positionDescription' },
          { text: this.lotTemplate.premiumType === PREMIUM_TYPES.FIX ? this.$t('Количество') : this.$t('Общее количество, тонн'), align: 'start', sortable: false, value: 'amount' },
          { text: this.lotTemplate.premiumType === PREMIUM_TYPES.FIX ? this.$t('Начальная цена за единицу в валюте Контракта') : this.$t('Стартовое значение дифференциала'), align: 'start', sortable: false, value: 'startPremium' },
          { text: this.$t('Действия'), align: 'center', sortable: false, value: 'positionTemplateId' }
        ]
        if (!this.showStartPremium) {
          headers = headers.filter(h => h.value !== 'startPremium')
        }
        return headers
      },
      showStartPremium () {
        return this.positions.some(p => !!p.startPremium)
      }
    },
    watch: {
      loading (newValue) {
        this.isLoading = newValue
      }
    },
    methods: {
      ...mapActions(POSITIONS_TEMPLATE_MANAGEMENT.NAMESPACE, {
        deletePositionAction: POSITIONS_TEMPLATE_MANAGEMENT.ACTIONS.DELETE_POSITION_TEMPLATE
      }),
      deletePosition: async function (positionTemplateId) {
        try {
          this.isLoading = true
          const model = {
            positionTemplateId: positionTemplateId,
            lotType: this.lotTemplate.lotType,
            lotTemplateId: this.lotTemplate.lotTemplateId
          }
          await Promise.all([
            this.deletePositionAction(model)
          ])

          this.isLoading = false
          this.edited()
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      openAddPositionDialog: function () {
        this.addPositionTemplateDialog.lotTemplate = this.lotTemplate
        this.addPositionTemplateDialog.callback = this.edited
        this.addPositionTemplateDialog.show = true
      },
      openEditPositionDialog: function (positionTemplateId, readonly) {
        this.editPositionTemplateDialog.positionTemplateId = positionTemplateId
        this.editPositionTemplateDialog.lotTemplate = this.lotTemplate
        this.editPositionTemplateDialog.callback = this.edited
        this.editPositionTemplateDialog.readonly = readonly
        this.editPositionTemplateDialog.show = true
      },
      openConfirmationBeforDelete: function (id) {
        this.confirmDialog.warning = true
        this.confirmDialog.additional = id
        this.confirmDialog.text = this.$t('Позиция шаблона будет удалена. Продолжить?')
        this.confirmDialog.callback = this.deletePosition
        this.confirmDialog.show = true
      },
      edited: function () {
        this.$emit('change')
      }
    }
  }
</script>
